import React, { useEffect, useMemo, useState } from "react";
import { useMsal } from "@azure/msal-react";
import DataTable, { TableColumn } from "react-data-table-component";
import { Bar } from "react-chartjs-2";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from "chart.js";
import { TrendingUp, Download, Info } from "lucide-react";
import * as XLSX from "xlsx";
import { getApiBaseUrl } from "../utils/baseUrl";
import { useLoading, acquireTokenSafe } from "../App";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

interface HubspotDeal {
    id: string;
    dealname?: string;
    amount?: string;
    dealstage?: string;
    pipeline?: string;
    closedate?: string;
    createdate?: string;
    hs_arr?: string;
    recurring_contracted_value_fy26?: string;
    total_renewal_fee?: string;
    total_renewal_fee_to_be_paid_annually?: string;
    hubspot_owner_id?: string;
}

interface Stage {
    id: string;
    label: string;
    displayOrder: number;
    isClosed: boolean;
    probability: number;
}

interface Pipeline {
    id: string;
    label: string;
    stages: Stage[];
}

interface DealsPayload {
    deals: HubspotDeal[];
    pipelines: Pipeline[];
    fetchedAt: string;
}

const num = (v?: string): number => {
    if (!v) return 0;
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : 0;
};

const eur = (n: number) =>
    n.toLocaleString("en-IE", {
        style: "currency",
        currency: "EUR",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    });

const customStyles = {
    table: { style: { backgroundColor: "var(--bg-secondary)", borderRadius: "8px" } },
    headRow: {
        style: {
            backgroundColor: "var(--bg-tertiary)",
            borderBottomColor: "var(--border-color)",
            color: "var(--text-primary)",
            fontWeight: 600,
            fontSize: "0.85rem",
        },
    },
    headCells: { style: { color: "var(--text-secondary)", paddingLeft: "16px", paddingRight: "16px" } },
    rows: {
        style: {
            backgroundColor: "var(--bg-secondary)",
            color: "var(--text-primary)",
            borderBottomColor: "var(--border-color)",
        },
        highlightOnHoverStyle: {
            backgroundColor: "var(--bg-hover)",
            color: "var(--text-primary)",
            borderBottomColor: "var(--border-color)",
        },
    },
    cells: { style: { color: "var(--text-primary)", paddingLeft: "16px", paddingRight: "16px" } },
    pagination: {
        style: {
            backgroundColor: "var(--bg-secondary)",
            color: "var(--text-secondary)",
            borderTopColor: "var(--border-color)",
        },
        pageButtonsStyle: {
            color: "var(--text-secondary)",
            fill: "var(--text-secondary)",
            "&:hover:not(:disabled)": { backgroundColor: "var(--bg-hover)" },
            "&:disabled": { opacity: 0.4 },
        },
    },
    noData: { style: { backgroundColor: "var(--bg-secondary)", color: "var(--text-secondary)" } },
};

const HubspotDealsPipeline: React.FC = () => {
    const { instance, accounts } = useMsal();
    const { setLoading } = useLoading();

    const [payload, setPayload] = useState<DealsPayload | null>(null);
    const [pipelineId, setPipelineId] = useState<string>("");
    const [stageFilter, setStageFilter] = useState<string>("all");
    const [showHelp, setShowHelp] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchData = async () => {
            const token = await acquireTokenSafe(instance, accounts);
            if (!token) return;
            setLoading(true);
            setError(null);
            try {
                const res = await fetch(`${getApiBaseUrl()}/api/hubspot/deals/data`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (!res.ok) throw new Error(`Request failed (${res.status})`);
                const data: DealsPayload = await res.json();
                setPayload(data);
                // Default to the pipeline holding the most deals.
                if (!pipelineId && data.pipelines.length > 0) {
                    const counts = new Map<string, number>();
                    for (const d of data.deals) {
                        if (d.pipeline) counts.set(d.pipeline, (counts.get(d.pipeline) || 0) + 1);
                    }
                    const best = data.pipelines
                        .map((p) => ({ id: p.id, c: counts.get(p.id) || 0 }))
                        .sort((a, b) => b.c - a.c)[0];
                    setPipelineId(best?.id || data.pipelines[0].id);
                }
            } catch (err: any) {
                setError(err?.message || "Failed to load HubSpot deals");
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [instance, accounts, setLoading]);

    const pipeline = useMemo(
        () => payload?.pipelines.find((p) => p.id === pipelineId) || null,
        [payload, pipelineId]
    );

    const stageMap = useMemo(() => {
        const m = new Map<string, Stage>();
        pipeline?.stages.forEach((s) => m.set(s.id, s));
        return m;
    }, [pipeline]);

    // Deals scoped to the current pipeline
    const pipelineDeals = useMemo(
        () => (payload?.deals || []).filter((d) => d.pipeline === pipelineId),
        [payload, pipelineId]
    );

    // KPI buckets
    const kpis = useMemo(() => {
        let openCount = 0,
            openValue = 0,
            openArr = 0,
            wonYtdValue = 0,
            wonYtdArr = 0;
        const yearStart = new Date(new Date().getFullYear(), 0, 1).getTime();
        for (const d of pipelineDeals) {
            const stage = d.dealstage ? stageMap.get(d.dealstage) : undefined;
            const amount = num(d.amount);
            const arr =
                num(d.hs_arr) ||
                num(d.recurring_contracted_value_fy26) ||
                num(d.total_renewal_fee_to_be_paid_annually) ||
                num(d.total_renewal_fee);
            if (!stage || !stage.isClosed) {
                openCount += 1;
                openValue += amount;
                openArr += arr;
            } else if (stage.probability === 1) {
                // Closed-won (HubSpot uses probability=1.0)
                const closed = d.closedate ? new Date(d.closedate).getTime() : 0;
                if (closed >= yearStart) {
                    wonYtdValue += amount;
                    wonYtdArr += arr;
                }
            }
        }
        return { openCount, openValue, openArr, wonYtdValue, wonYtdArr };
    }, [pipelineDeals, stageMap]);

    // Stage-level counts / sums for the chart and stage chips
    const byStage = useMemo(() => {
        if (!pipeline) return [];
        const map = new Map<string, { stage: Stage; count: number; value: number; arr: number }>();
        pipeline.stages.forEach((s) => map.set(s.id, { stage: s, count: 0, value: 0, arr: 0 }));
        for (const d of pipelineDeals) {
            if (!d.dealstage) continue;
            const row = map.get(d.dealstage);
            if (!row) continue;
            row.count += 1;
            row.value += num(d.amount);
            row.arr +=
                num(d.hs_arr) ||
                num(d.recurring_contracted_value_fy26) ||
                num(d.total_renewal_fee_to_be_paid_annually) ||
                num(d.total_renewal_fee);
        }
        return Array.from(map.values()).sort((a, b) => a.stage.displayOrder - b.stage.displayOrder);
    }, [pipeline, pipelineDeals]);

    const filteredDeals = useMemo(
        () => (stageFilter === "all" ? pipelineDeals : pipelineDeals.filter((d) => d.dealstage === stageFilter)),
        [pipelineDeals, stageFilter]
    );

    type Row = HubspotDeal & {
        amountNum: number;
        arrNum: number;
        stageLabel: string;
        isClosedWon: boolean;
    };
    const rows: Row[] = useMemo(
        () =>
            filteredDeals.map((d) => {
                const stage = d.dealstage ? stageMap.get(d.dealstage) : undefined;
                return {
                    ...d,
                    amountNum: num(d.amount),
                    arrNum:
                        num(d.hs_arr) ||
                        num(d.recurring_contracted_value_fy26) ||
                        num(d.total_renewal_fee_to_be_paid_annually) ||
                        num(d.total_renewal_fee),
                    stageLabel: stage?.label || d.dealstage || "—",
                    isClosedWon: !!stage && stage.isClosed && stage.probability === 1,
                };
            }),
        [filteredDeals, stageMap]
    );

    const columns: TableColumn<Row>[] = [
        {
            name: "Deal",
            selector: (r) => r.dealname || "",
            sortable: true,
            grow: 3,
            cell: (r) => <span className="cell-primary">{r.dealname || "—"}</span>,
        },
        {
            name: "Stage",
            selector: (r) => r.stageLabel,
            sortable: true,
            grow: 2,
            cell: (r) => (
                <span
                    className="cell-secondary"
                    style={r.isClosedWon ? { color: "var(--accent-primary)", fontWeight: 600 } : undefined}
                >
                    {r.stageLabel}
                </span>
            ),
        },
        {
            name: "Amount",
            selector: (r) => r.amountNum,
            sortable: true,
            right: true,
            cell: (r) => <span className="cell-value">{r.amountNum ? eur(r.amountNum) : "—"}</span>,
        },
        {
            name: "ARR",
            selector: (r) => r.arrNum,
            sortable: true,
            right: true,
            cell: (r) => (
                <span className="cell-value accent">{r.arrNum ? eur(r.arrNum) : "—"}</span>
            ),
        },
        {
            name: "Close Date",
            selector: (r) => r.closedate || "",
            sortable: true,
            cell: (r) => (
                <span className="cell-secondary">
                    {r.closedate ? new Date(r.closedate).toLocaleDateString("en-IE") : "—"}
                </span>
            ),
        },
        {
            name: "Created",
            selector: (r) => r.createdate || "",
            sortable: true,
            cell: (r) => (
                <span className="cell-secondary">
                    {r.createdate ? new Date(r.createdate).toLocaleDateString("en-IE") : "—"}
                </span>
            ),
        },
    ];

    const chartData = useMemo(
        () => ({
            labels: byStage.map((s) => s.stage.label),
            datasets: [
                {
                    label: "Deals",
                    data: byStage.map((s) => s.count),
                    backgroundColor: "rgba(99, 102, 241, 0.6)",
                    borderColor: "rgba(99, 102, 241, 1)",
                    borderWidth: 1,
                    yAxisID: "y",
                },
                {
                    label: "Value (€)",
                    data: byStage.map((s) => Math.round(s.value)),
                    backgroundColor: "rgba(16, 185, 129, 0.6)",
                    borderColor: "rgba(16, 185, 129, 1)",
                    borderWidth: 1,
                    yAxisID: "y1",
                },
            ],
        }),
        [byStage]
    );

    const chartOpts = useMemo(
        () => ({
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: { labels: { color: "var(--text-secondary)" } },
                tooltip: {
                    callbacks: {
                        label: (ctx: any) => {
                            if (ctx.dataset.label === "Value (€)") return `Value: ${eur(ctx.parsed.y)}`;
                            return `Deals: ${ctx.parsed.y}`;
                        },
                    },
                },
            },
            scales: {
                x: {
                    ticks: { color: "var(--text-secondary)", maxRotation: 45, minRotation: 0 },
                    grid: { color: "var(--border-color)" },
                },
                y: {
                    type: "linear" as const,
                    position: "left" as const,
                    ticks: { color: "var(--text-secondary)", precision: 0 },
                    grid: { color: "var(--border-color)" },
                    title: { display: true, text: "Deals", color: "var(--text-secondary)" },
                },
                y1: {
                    type: "linear" as const,
                    position: "right" as const,
                    ticks: {
                        color: "var(--text-secondary)",
                        callback: (v: any) => `€${(v / 1000).toFixed(0)}k`,
                    },
                    grid: { drawOnChartArea: false },
                    title: { display: true, text: "Value (€)", color: "var(--text-secondary)" },
                },
            },
        }),
        []
    );

    const downloadXLSX = () => {
        const wb = XLSX.utils.book_new();
        const summary = byStage.map((s) => ({
            Stage: s.stage.label,
            Deals: s.count,
            "Value (EUR)": Math.round(s.value),
            "ARR (EUR)": Math.round(s.arr),
            "Closed?": s.stage.isClosed ? "Yes" : "No",
            Probability: s.stage.probability,
        }));
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(summary), "By Stage");

        const detail = rows.map((r) => ({
            Deal: r.dealname || "",
            Stage: r.stageLabel,
            "Amount (EUR)": r.amountNum || null,
            "ARR (EUR)": r.arrNum || null,
            "Close Date": r.closedate ? r.closedate.split("T")[0] : "",
            "Created": r.createdate ? r.createdate.split("T")[0] : "",
            "HubSpot ID": r.id,
        }));
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(detail), "Deals");

        const pname = (pipeline?.label || "deals").toLowerCase().replace(/\s+/g, "_");
        XLSX.writeFile(wb, `hubspot_${pname}_${new Date().toISOString().split("T")[0]}.xlsx`);
    };

    return (
        <div className="page-container">
            <div className="page-header compact">
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <div className="page-title">
                        <TrendingUp size={20} />
                        <h1>HubSpot Deals Pipeline</h1>
                    </div>
                    <div style={{ display: "flex", gap: 8 }}>
                        <button
                            className="download-btn"
                            onClick={() => setShowHelp((v) => !v)}
                            title="Notes on Pipeline"
                        >
                            <Info size={16} /> {showHelp ? "Hide details" : "Notes"}
                        </button>
                        <button className="download-btn" onClick={downloadXLSX} title="Export as XLSX">
                            <Download size={16} /> XLSX
                        </button>
                    </div>
                </div>
            </div>

            {showHelp && (
                <div
                    style={{
                        background: "var(--bg-secondary)",
                        border: "1px solid var(--border-color)",
                        borderLeft: "3px solid var(--accent-primary)",
                        borderRadius: 6,
                        padding: "14px 18px",
                        marginBottom: 20,
                        color: "var(--text-secondary)",
                        fontSize: 13,
                        lineHeight: 1.55,
                    }}
                >
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            marginBottom: 8,
                            color: "var(--text-primary)",
                            fontWeight: 600,
                            fontSize: 14,
                        }}
                    >
                        <Info size={16} /> How this report is built
                    </div>
                    <ul style={{ margin: "0 0 0 20px", padding: 0 }}>
                        <li>
                            Deals are pulled live from HubSpot via{" "}
                            <code>/crm/v3/objects/deals</code>, cached server-side for 10 minutes.
                        </li>
                        <li>
                            <strong style={{ color: "var(--text-primary)" }}>Open Pipeline Value</strong> sums{" "}
                            <code>amount</code> across all deals whose stage is not flagged closed.
                        </li>
                        <li>
                            <strong style={{ color: "var(--text-primary)" }}>ARR</strong> takes the first non-zero of{" "}
                            <code>hs_arr</code>, <code>recurring_contracted_value_fy26</code>,{" "}
                            <code>total_renewal_fee_to_be_paid_annually</code>, then <code>total_renewal_fee</code>.
                        </li>
                        <li>
                            <strong style={{ color: "var(--text-primary)" }}>Won YTD</strong> = deals in a closed-won
                            stage (probability = 1.0) with a <code>closedate</code> in the current calendar year.
                        </li>
                        <li>Pipelines and stage labels come from <code>/crm/v3/pipelines/deals</code>.</li>
                    </ul>
                </div>
            )}

            {/* Pipeline selector */}
            {payload && payload.pipelines.length > 1 && (
                <div className="table-controls" style={{ marginBottom: 16 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <label
                            style={{ color: "var(--text-secondary)", fontSize: 13 }}
                            htmlFor="pipeline-select"
                        >
                            Pipeline:
                        </label>
                        <select
                            id="pipeline-select"
                            value={pipelineId}
                            onChange={(e) => {
                                setPipelineId(e.target.value);
                                setStageFilter("all");
                            }}
                            className="filter-select"
                            style={{ padding: "6px 10px", borderRadius: 4 }}
                        >
                            {payload.pipelines.map((p) => (
                                <option key={p.id} value={p.id}>
                                    {p.label}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            )}

            {/* KPI cards */}
            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                    gap: 12,
                    marginBottom: 20,
                }}
            >
                <KpiCard label="Open Deals" value={kpis.openCount.toLocaleString()} sub={pipeline?.label || ""} />
                <KpiCard label="Open Pipeline Value" value={eur(kpis.openValue)} sub="Sum of amount on open deals" />
                <KpiCard label="Open ARR" value={eur(kpis.openArr)} sub="Recurring revenue on open deals" />
                <KpiCard
                    label="Won YTD"
                    value={eur(kpis.wonYtdValue)}
                    sub={`${eur(kpis.wonYtdArr)} ARR`}
                    accent
                />
            </div>

            {/* Stage chart */}
            {byStage.length > 0 && (
                <div
                    style={{
                        background: "var(--bg-secondary)",
                        border: "1px solid var(--border-color)",
                        borderRadius: 8,
                        padding: 16,
                        marginBottom: 20,
                        height: 320,
                    }}
                >
                    <Bar data={chartData} options={chartOpts} />
                </div>
            )}

            {/* Stage filter chips */}
            {pipeline && (
                <div
                    style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 6,
                        marginBottom: 16,
                    }}
                >
                    <button
                        className={`filter-select${stageFilter === "all" ? " active" : ""}`}
                        onClick={() => setStageFilter("all")}
                        style={{
                            padding: "6px 12px",
                            border: "1px solid var(--border-color)",
                            borderRadius: 16,
                            background: stageFilter === "all" ? "var(--accent-primary)" : "var(--bg-secondary)",
                            color: stageFilter === "all" ? "#fff" : "var(--text-secondary)",
                            cursor: "pointer",
                            fontSize: 13,
                            fontWeight: stageFilter === "all" ? 600 : 400,
                        }}
                    >
                        All stages
                    </button>
                    {byStage.map((s) => {
                        const active = stageFilter === s.stage.id;
                        return (
                            <button
                                key={s.stage.id}
                                onClick={() => setStageFilter(s.stage.id)}
                                style={{
                                    padding: "6px 12px",
                                    border: "1px solid var(--border-color)",
                                    borderRadius: 16,
                                    background: active ? "var(--accent-primary)" : "var(--bg-secondary)",
                                    color: active ? "#fff" : "var(--text-secondary)",
                                    cursor: "pointer",
                                    fontSize: 13,
                                    fontWeight: active ? 600 : 400,
                                }}
                            >
                                {s.stage.label} ({s.count})
                            </button>
                        );
                    })}
                </div>
            )}

            {error && (
                <div
                    style={{
                        background: "var(--bg-secondary)",
                        border: "1px solid var(--status-error, #d9534f)",
                        borderRadius: 6,
                        padding: "10px 14px",
                        marginBottom: 12,
                        color: "var(--status-error, #d9534f)",
                    }}
                >
                    {error}
                </div>
            )}

            <div
                style={{
                    background: "var(--bg-secondary)",
                    borderRadius: 8,
                    border: "1px solid var(--border-color)",
                }}
            >
                <DataTable
                    columns={columns}
                    data={rows}
                    customStyles={customStyles}
                    pagination
                    paginationPerPage={25}
                    paginationRowsPerPageOptions={[10, 25, 50, 100]}
                    highlightOnHover
                    defaultSortFieldId={3}
                    defaultSortAsc={false}
                    noDataComponent={
                        <div style={{ padding: 40, textAlign: "center", color: "var(--text-muted)" }}>
                            {payload === null
                                ? "Loading…"
                                : pipelineDeals.length === 0
                                ? "No deals in the selected pipeline."
                                : "No deals match the current stage filter."}
                        </div>
                    }
                />
            </div>
        </div>
    );
};

const KpiCard: React.FC<{ label: string; value: string; sub?: string; accent?: boolean }> = ({
    label,
    value,
    sub,
    accent,
}) => (
    <div
        style={{
            background: accent ? "var(--bg-tertiary)" : "var(--bg-secondary)",
            border: accent ? "1px solid var(--accent-primary)" : "1px solid var(--border-color)",
            borderRadius: 8,
            padding: 14,
        }}
    >
        <div
            style={{
                color: accent ? "var(--accent-primary)" : "var(--text-secondary)",
                fontSize: 12,
                marginBottom: 4,
                fontWeight: accent ? 600 : 400,
                letterSpacing: accent ? 0.3 : undefined,
                textTransform: accent ? "uppercase" : undefined,
            }}
        >
            {label}
        </div>
        <div style={{ fontSize: 22, fontWeight: 600, color: "var(--text-primary)" }}>{value}</div>
        {sub && (
            <div style={{ color: "var(--text-secondary)", fontSize: 13, marginTop: 2 }}>{sub}</div>
        )}
    </div>
);

export default HubspotDealsPipeline;
